import * as React from 'react';
import { ElementLoader, PromptConfirmation, RadioSymbol } from 'ui';
import { ModalWrapper } from 'app/components/modal-wrapper/modal-wrapper';
import { replaceTemplateString } from 'app/infrastructure/platform';
import {
  emptyUsers, hasFieldValueChanged, isDisabled
} from 'app/area/favourite-administration/list/change-users/change-users-helpers';
import { SelectMultiple } from 'app/area/shared/translated-components';
import { ChangeOptionType } from 'app/area/favourite-administration/list/types';
import { ChangeUsersComponentProps, ChangeUsersField } from 'app/area/favourite-administration/list/change-users/types';
import { FavouriteListIds } from 'app/area/favourite-administration/list/constants';
import { ComplexValue, LoadOptions } from 'app/types-shared';
import { endpoints } from 'app/area/favourite-administration/list/endpoints';
import { ApplyOnDescendants } from 'app/area/favourite-administration/list/components/apply-on-descendants';
import styles from './change-users.scss';

interface ChangeUsersComponentState {
  shouldDisplayModal: boolean;
}

export class ChangeUsersModal extends React.PureComponent<ChangeUsersComponentProps, ChangeUsersComponentState> {

  constructor(props: ChangeUsersComponentProps) {
    super(props);

    this.state = {
      shouldDisplayModal: undefined
    };
  }

  public componentWillUnmount(): void {
    this.props.clearChangeUsers();
  }

  public componentDidUpdate(prevProps: ChangeUsersComponentProps) {
    this.handleIsDirty(prevProps);
  }

  private handleIsDirty = (prevProps: ChangeUsersComponentProps) => {
    hasFieldValueChanged(prevProps, this.props) && this.props.updateIsDirty(true);
  };

  private toggleModal = () => {
    this.setState({ shouldDisplayModal: !this.state.shouldDisplayModal });
  };

  private continue = () => {
    this.toggleModal();
    this.props.save();
    this.props.updateIsDirty(false);
  };

  private onClose = () => {
    this.props.onClose();
    this.props.updateIsDirty(false);
  };

  private addUsers = () => {
    this.props.updateByKey(ChangeUsersField.replaceExistingUsers, undefined);
    this.props.resetValidation(FavouriteListIds.sharedWithUsers);
    this.props.updateState(ChangeOptionType.Add);
  };

  private replaceUsers = () => {
    this.props.updateByKey(ChangeUsersField.replaceExistingUsers, undefined);
    this.props.resetValidation(FavouriteListIds.sharedWithUsers);
    this.props.updateState(ChangeOptionType.Replace);
  };

  private onChange = (values: ComplexValue<number>[]) => {
    this.props.resetValidation(FavouriteListIds.sharedWithUsers);
    this.props.updateByKey(ChangeUsersField.replaceExistingUsers, values);
  };

  private onLoadOptions = (loadOptionsParams: LoadOptions) => {
    this.props.loadOptions(FavouriteListIds.sharedWithUsers, endpoints.sharedWithUsersOptions, loadOptionsParams);
  };

  private onApplyOnDescendants = (value: number) => {
    this.props.updateByKey(ChangeUsersField.applyOnDescendants, !!value);
  };

  public render() {
    const { list: {
      changeUsers, fetchingChangeUsers, numberOfResults, editableNumberOfResults
    }, options, translation, validation } = this.props;

    const selectAddUsers = changeUsers.state === ChangeOptionType.Add;
    const selectReplaceUsers = changeUsers.state === ChangeOptionType.Replace;

    const picker = <SelectMultiple
      componentId={ChangeUsersField.replaceExistingUsers}
      className={'w-100 mb-1'}
      displaySelectAll={true}
      locale={this.props.locale}
      valueItems={changeUsers?.form?.[ChangeUsersField.replaceExistingUsers] || []}
      options={options?.[FavouriteListIds.sharedWithUsers]?.codes as ComplexValue<number>[] || []}
      placeholder={translation?.search}
      fetching={options?.[FavouriteListIds.sharedWithUsers]?.fetching}
      onLoadOptions={this.onLoadOptions}
      onChange={this.onChange}
      validation={validation?.sharedWithUsers} />;

    return (<>
      <ModalWrapper
        headerTitle={`${translation.change_users} (${numberOfResults})`}
        btnTitle={translation.save}
        btnCancelTitle={translation.cancel}
        onClose={this.onClose}
        onDone={this.toggleModal}
        closeOnDone={false}
        isCancelDisabled={fetchingChangeUsers}
        isDisabled={fetchingChangeUsers || emptyUsers(this.props) || isDisabled(this.props)}
        canCloseOnBackground={!fetchingChangeUsers}
        canCloseOnXButton={!fetchingChangeUsers}>

        {fetchingChangeUsers
          ? <ElementLoader />
          : <div>
            <p className={styles.info}>{translation.shared_with_users_info}</p>

            <div className="row">
              <div className="col-12">
                <div className={styles.container}>
                  <div className="form-check">
                    <input
                      id="add-users"
                      type="radio"
                      className="form-check-input visuallyhidden"
                      value={ChangeOptionType.Add}
                      checked={selectAddUsers}
                      onChange={this.addUsers} />
                    <label
                      htmlFor="add-users"
                      className="form-check-label">
                      <RadioSymbol />
                      <b>{translation.add_users}</b>
                    </label>
                    {selectAddUsers ? picker : null}

                    <input
                      id="replace-users"
                      type="radio"
                      className="form-check-input visuallyhidden"
                      value={ChangeOptionType.Replace}
                      checked={selectReplaceUsers}
                      onChange={this.replaceUsers} />
                    <label
                      htmlFor="replace-users"
                      className="form-check-label">
                      <RadioSymbol />
                      <b>{translation.replace_existing_users}</b>
                    </label>
                    {selectReplaceUsers ? picker : null}
                  </div>
                </div>
                <ApplyOnDescendants
                  value={changeUsers?.form?.[ChangeUsersField.applyOnDescendants]}
                  onChange={this.onApplyOnDescendants}
                  translation={translation} />
              </div>
            </div>
          </div>}
      </ModalWrapper>

      {this.state.shouldDisplayModal && <PromptConfirmation
        title={translation.change_users}
        description={replaceTemplateString(
          translation.change_users_confirmation_description,
          editableNumberOfResults
        )}
        continueText={translation.change_users_done}
        cancelText={translation.cancel}
        onContinue={this.continue}
        onCancel={this.toggleModal}
      />}
    </>);
  }
}
